import { addDays, differenceInCalendarDays, startOfDay } from "date-fns";
import type { DateRange } from "react-day-picker";
import { toDateString } from "./calendar-window";

export interface PdpCalendarDayDisabledOptions {
  availableDates?: ReadonlySet<string> | readonly string[];
  isAvailabilityPending?: boolean;
  selectedRange?: DateRange;
  today: Date;
  maxDate: Date;
}

const toAllowlist = (
  availableDates?: ReadonlySet<string> | readonly string[],
): ReadonlySet<string> | undefined => {
  if (availableDates == null) return undefined;
  return availableDates instanceof Set
    ? availableDates
    : new Set(availableDates);
};

/** Sleep nights are [checkIn, checkOut) — checkout morning is not a stay night. */
const areSleepNightsAvailable = (
  checkIn: Date,
  checkOut: Date,
  available: ReadonlySet<string>,
): boolean => {
  const nights = differenceInCalendarDays(checkOut, checkIn);
  for (let i = 0; i < nights; i++) {
    if (!available.has(toDateString(addDays(checkIn, i)))) return false;
  }
  return true;
};

/**
 * Disable matcher for the PDP datepicker.
 *
 * - Pending with no allowlist: fail closed (every day in the window).
 * - Error / no allowlist: fail open (only today/maxDate bounds apply).
 * - With data: only `availableDates` are selectable. After check-in, a
 *   checkout day must still be on the allowlist and every sleep night in
 *   `[checkIn, checkOut)` must be available (no spanning reserved gaps).
 */
export function isPdpCalendarDayDisabled(
  date: Date,
  {
    availableDates,
    isAvailabilityPending = false,
    selectedRange,
    today,
    maxDate,
  }: PdpCalendarDayDisabledOptions,
): boolean {
  const day = startOfDay(date);
  if (day < startOfDay(today)) return true;
  if (day > startOfDay(maxDate)) return true;

  const allowlist = toAllowlist(availableDates);
  if (!allowlist) return Boolean(isAvailabilityPending);

  const ymd = toDateString(day);
  const from = selectedRange?.from
    ? startOfDay(selectedRange.from)
    : undefined;
  const to = selectedRange?.to ? startOfDay(selectedRange.to) : undefined;
  const pickingCheckout = Boolean(from && !to);

  if (!pickingCheckout) {
    return !allowlist.has(ymd);
  }

  // Keep the selected check-in day enabled while choosing checkout.
  if (day.getTime() === from!.getTime()) return false;

  // Never light up nights outside the allowlist (including the morning after
  // the last available sleep night).
  if (!allowlist.has(ymd)) return true;

  if (day < from!) return false;

  return !areSleepNightsAvailable(from!, day, allowlist);
}
